import { useQuery } from "@tanstack/react-query";
import type { SourceStage } from "@patent-studio/api-types";
import { SourcesPanel } from "@/components/patterns/SourcesPanel";
import { stageSourcesKey } from "@/hooks/query-keys";
import { useTask } from "@/hooks/use-tasks";
import { readSources } from "@/services/sources";
import { findStage, THESIS_SUB_STEPS } from "@/v2/stage-vocabulary";

/** The rail's name for a stage, which the panel's title names it by. */
function railName(stage: SourceStage): string {
  const named = (id: string) => findStage(id)?.label ?? id;
  const thesis = (id: string) =>
    THESIS_SUB_STEPS.find((step) => step.id === id)?.label ?? id;
  switch (stage) {
    case "classification":
      return named("intake");
    case "invention":
      return thesis("invention");
    case "novelty":
      return thesis("novelty");
    case "figures":
      return named("figures");
    case "claim_strategy":
      return named("claim-strategy");
    case "specification":
      return named("planning-outline");
    case "open_questions":
      return "Open Questions";
    case "generate":
      return named("generate");
  }
}

/**
 * The files a stage read its output from, under the stage's heading. Drawn once the server
 * has said, and not at all while it has not: a panel of nothing would read as a stage that
 * used nothing. A locked stage and a draft in progress draw other pages, which hold none.
 */
export function StageSources({
  taskId,
  stage,
}: {
  /** Undefined until the route names the draft, and nothing is asked for until it does. */
  taskId: string | undefined;
  stage: SourceStage;
}) {
  // A write on the page patches the cached task rather than reading it again, so the files as the
  // task holds them are part of the key: a file added, typed, read or removed asks again.
  const { data: task } = useTask(taskId ?? null);
  const files = (task?.files ?? [])
    .map((f) =>
      [f.file_id, f.read_status, f.literature_type, f.supporting_tag].join(":"),
    )
    .join("|");
  const { data } = useQuery({
    queryKey: [...stageSourcesKey(taskId ?? "", stage), files],
    queryFn: () => readSources(taskId ?? "", stage),
    enabled: taskId !== undefined,
    // The last answer stays while the same stage of the same draft asks again, never another's.
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      return key !== undefined && key[1] === taskId && key[3] === stage
        ? previous
        : undefined;
    },
  });
  if (data === undefined) return null;
  return <SourcesPanel stage={railName(stage)} sources={data} />;
}
